import { create } from 'zustand'
import type { AdminUser } from '@/api/auth'
import { adminAuthApi } from '@/api/auth'
import { ApiError } from '@/api/client'

const REFRESH_TOKEN_KEY = 'admin-auth-refresh'

export type LoginResult =
  | { status: 'success' }
  | { status: 'not_admin' }
  | { status: 'error'; message: string }

interface AuthState {
  user: AdminUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean

  // Actions
  login: (email: string, otp: string) => Promise<LoginResult>
  logout: () => void
  restoreSession: () => Promise<boolean>
  refreshAccessToken: () => Promise<boolean>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,

  /**
   * Three-phase login:
   * 1. Verify OTP -> get tokens
   * 2. Call getAdminMe -> verify platform admin role
   * 3. Only then set isAuthenticated=true
   */
  login: async (email: string, otp: string): Promise<LoginResult> => {
    try {
      // Phase 1: Verify OTP and get tokens
      const otpResponse = await adminAuthApi.verifyOtp(email, otp)

      // Phase 2: Temporarily set token so API client can use it for getAdminMe
      set({ token: otpResponse.access_token })

      try {
        // Phase 2: Verify platform admin role
        const adminUser = await adminAuthApi.getAdminMe()

        // Phase 3: Success -- save refresh token and set full auth state
        localStorage.setItem(REFRESH_TOKEN_KEY, otpResponse.refresh_token)
        set({
          user: adminUser,
          token: otpResponse.access_token,
          isAuthenticated: true,
          isLoading: false,
        })

        return { status: 'success' }
      } catch (adminError: unknown) {
        // getAdminMe failed -- clear temporary token
        set({ token: null, isAuthenticated: false, isLoading: false })

        // 403 means user is not a platform admin
        // Defensive: check .status directly (instanceof can break with HMR)
        const errStatus =
          adminError instanceof ApiError
            ? adminError.status
            : adminError &&
                typeof adminError === 'object' &&
                'status' in adminError
              ? (adminError as { status: number }).status
              : undefined

        if (errStatus === 403) {
          // Best-effort server logout to clean up the tokens
          adminAuthApi.logoutServer(
            otpResponse.access_token,
            otpResponse.refresh_token
          )
          return { status: 'not_admin' }
        }

        throw adminError
      }
    } catch (error) {
      // Clear any partial state
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      })

      if (error instanceof ApiError) {
        return { status: 'error', message: error.message }
      }

      // LoginResult 'not_admin' was already returned above, so this is a real error
      if (
        error &&
        typeof error === 'object' &&
        'status' in error &&
        (error as LoginResult).status === 'not_admin'
      ) {
        return error as LoginResult
      }

      return {
        status: 'error',
        message:
          error instanceof Error ? error.message : 'An unexpected error occurred',
      }
    }
  },

  logout: () => {
    const { token } = get()
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)

    // Best-effort server logout
    if (token && refreshToken) {
      adminAuthApi.logoutServer(token, refreshToken)
    }

    localStorage.removeItem(REFRESH_TOKEN_KEY)
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    })
  },

  /**
   * Restore session from localStorage refresh token.
   * Called once on app startup.
   * 1. Refresh the access token
   * 2. Verify platform admin status via getAdminMe
   */
  restoreSession: async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!refreshToken) {
      set({ isLoading: false })
      return false
    }

    try {
      // Get new access token
      const response = await adminAuthApi.refreshToken(refreshToken)

      // Save rotated refresh token
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token)

      // Set token temporarily so getAdminMe can use it
      set({ token: response.access_token })

      // Verify still a platform admin
      const adminUser = await adminAuthApi.getAdminMe()

      set({
        user: adminUser,
        token: response.access_token,
        isAuthenticated: true,
        isLoading: false,
      })

      return true
    } catch {
      // Session restore failed -- clear everything
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      })
      return false
    }
  },

  /**
   * Refresh the access token using the stored refresh token.
   * Called by the API client on 401 responses.
   */
  refreshAccessToken: async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!refreshToken) {
      return false
    }

    try {
      const response = await adminAuthApi.refreshToken(refreshToken)
      // Rotate refresh token in localStorage
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token)
      // Update access token in memory only
      set({ token: response.access_token })
      return true
    } catch {
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      })
      return false
    }
  },
}))
